/**
 * MenuBar right side: agent count, cloud dots, FLZ, clock
 */
import { Cloud, Cpu, Moon, Sun, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { useOSTheme } from '../OSThemeContext'
import type { AgentStats } from './useMenuBarData'

interface Props {
  agentStats: AgentStats | null
  flzBalance: number
}

export default function MenuBarStatus({ agentStats, flzBalance }: Props) {
  const { user } = useAuth()
  const [time, setTime] = useState(new Date())
  const { theme, toggle } = useOSTheme()

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const timeStr = time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  const dateStr = time.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

  return (
    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '14px', fontSize: '11px', color: 'var(--os-muted)' }}>
      {/* Agent count */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 5px #10b981' }} />
        <span>{agentStats?.active_agents ?? '—'} agents</span>
      </div>

      {/* Cloud indicators */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Cloud size={11} color="#f6821f" />
        <span style={{ color: '#f6821f' }}>CF</span>
        <Cpu size={11} color="#0078d4" />
        <span style={{ color: '#0078d4' }}>Azure</span>
      </div>

      {/* Level & XP */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
         <span style={{ fontWeight: 800, color: '#6366f1' }}>Lvl {user?.level || 1}</span>
         <div style={{ width: '60px', height: '5px', background: 'var(--os-input-bg)', borderRadius: '3px', overflow: 'hidden', border: '1px solid var(--os-border)' }}>
             <div style={{
               width: `${Math.min(100, ((user?.total_xp || 0) % 1000) / 10)}%`,
               height: '100%',
               background: 'linear-gradient(90deg, #6366f1, #a855f7)',
             }} />
         </div>
      </div>

      {/* FLZ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#f59e0b' }}>
        <Zap size={11} />
        <span>{flzBalance.toLocaleString('en-US', { maximumFractionDigits: 0 })} FLZ</span>
      </div>

      {/* Dark/Light toggle */}
      <button
        onClick={toggle}
        title={theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--os-muted)',
          display: 'flex',
          alignItems: 'center',
          padding: '2px',
          borderRadius: '4px',
        }}
      >
        {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
      </button>

      {/* Clock */}
      <span>{dateStr} {timeStr}</span>
    </div>
  )
}
